import React from "react";
import { Card, Row, Col, Spin, Empty, Typography, Tooltip, Progress } from "antd";
import {
  SendOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  LinkOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

const CampaignFunnelCards = ({ campaigns, loading }) => {
  if (loading) {
    return (
      <Card title="Performance por Campanha">
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!campaigns?.campaigns || campaigns.campaigns.length === 0) {
    return (
      <Card title="Performance por Campanha">
        <Empty description="Nenhuma campanha encontrada no periodo selecionado" />
      </Card>
    );
  }

  const truncateSubject = (subject, maxLength = 50) => {
    if (subject.length <= maxLength) return subject;
    return subject.substring(0, maxLength) + "...";
  };

  const FunnelStep = ({ icon, label, value, percentage, color, isLast }) => (
    <div style={{ position: "relative", marginBottom: isLast ? 0 : 8 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "8px 12px",
          backgroundColor: `${color}15`,
          borderLeft: `3px solid ${color}`,
          borderRadius: "0 4px 4px 0",
        }}
      >
        <span style={{ color, marginRight: 8 }}>{icon}</span>
        <Text style={{ flex: 1, fontSize: 12 }}>{label}</Text>
        <Text strong style={{ marginRight: 8 }}>
          {value.toLocaleString()}
        </Text>
        {percentage !== null && (
          <Text type="secondary" style={{ fontSize: 11, minWidth: 45 }}>
            ({percentage.toFixed(1)}%)
          </Text>
        )}
      </div>
      {!isLast && (
        <div
          style={{
            position: "absolute",
            left: 18,
            bottom: -8,
            width: 0,
            height: 0,
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderTop: `8px solid ${color}40`,
          }}
        />
      )}
    </div>
  );

  const MetricBadge = ({ label, value, color }) => (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 8px",
        backgroundColor: `${color}15`,
        borderRadius: 4,
        marginRight: 8,
        marginBottom: 4,
      }}
    >
      <Text style={{ fontSize: 11, color: "#666", marginRight: 4 }}>{label}:</Text>
      <Text strong style={{ fontSize: 12, color }}>
        {value.toFixed(1)}%
      </Text>
    </div>
  );

  return (
    <Card
      title="Performance por Campanha"
      extra={
        <Text type="secondary" style={{ fontSize: 12 }}>
          {campaigns.startDate} a {campaigns.endDate}
        </Text>
      }
    >
      <Row gutter={[16, 16]}>
        {campaigns.campaigns.map((campaign, index) => (
          <Col xs={24} sm={12} lg={8} xl={6} key={index}>
            <Card
              size="small"
              style={{
                height: "100%",
                borderRadius: 8,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              bodyStyle={{ padding: 12 }}
            >
              <Tooltip title={campaign.subject}>
                <Title
                  level={5}
                  style={{
                    marginBottom: 12,
                    fontSize: 13,
                    lineHeight: 1.4,
                    height: 36,
                    overflow: "hidden",
                  }}
                >
                  {truncateSubject(campaign.subject)}
                </Title>
              </Tooltip>

              {/* Funnel Visualization */}
              <div style={{ marginBottom: 12 }}>
                <FunnelStep
                  icon={<SendOutlined />}
                  label="Enviados"
                  value={campaign.send}
                  percentage={null}
                  color="#1890ff"
                />
                <FunnelStep
                  icon={<CheckCircleOutlined />}
                  label="Entregues"
                  value={campaign.delivery}
                  percentage={campaign.deliveryRate}
                  color="#52c41a"
                />
                <FunnelStep
                  icon={<EyeOutlined />}
                  label="Abertos"
                  value={campaign.open}
                  percentage={campaign.openRate}
                  color="#13c2c2"
                />
                <FunnelStep
                  icon={<LinkOutlined />}
                  label="Cliques"
                  value={campaign.click}
                  percentage={campaign.clickRate}
                  color="#722ed1"
                  isLast
                />
              </div>

              {/* Visual Progress */}
              <div style={{ marginBottom: 8 }}>
                <Progress
                  percent={campaign.openRate || 0}
                  size="small"
                  strokeColor="#13c2c2"
                  format={() => `${(campaign.openRate || 0).toFixed(0)}% abertos`}
                />
              </div>

              {/* Summary Metrics */}
              <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 8 }}>
                <MetricBadge label="CTR" value={campaign.ctr || 0} color="#722ed1" />
                {campaign.bounceRate > 0 && (
                  <MetricBadge
                    label="Bounce"
                    value={campaign.bounceRate}
                    color="#fa8c16"
                  />
                )}
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </Card>
  );
};

export default CampaignFunnelCards;
